import * as Sentry from "@sentry/react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useGetLinkedChapter } from "src/hooks/useGetLinkedDocuments";
import { useOnScreen } from "src/hooks/useOnScreen";
import { useSeriesStore } from "src/stores/seriesStore";
import { useStoryBibleStore } from "src/stores/storyBibleStore";
import { POVS, isAutoPOV } from "../../../../../../../src/shared/constants";
import Auth from "../../../../../Auth";
import { useProjectStore } from "../../../../../stores/projectStore";
import { sendAppEvent } from "../../../../../Utils";
import { useToast } from "../../../../ToastWrapper";
import { TenseAndPOV } from "../../../TenseAndPOV";
import { useStructuredOutlineStore } from "../../StructuredOutlineStore";
import { useOutlinePOVTense } from "../../useOutlinePOVTense";

export const ChapterTensePOVBar = memo(({ chapter, projectId: fallbackProjectId, outlineProjectId = fallbackProjectId, disabled = false, allowWrap = false }) => {
  const document = useProjectStore((state) => state.document);
  const currentDocumentPOVTense = useProjectStore((state) => state.currentDocumentPOVTense);
  const setCurrentDocumentPOVTense = useProjectStore((state) => state.setCurrentDocumentPOVTense);
  const selectedBook = useOutlinePOVTense(outlineProjectId);
  const projectId = selectedBook.projectId || fallbackProjectId;
  const multiplePOV = selectedBook.isMultiplePOV.value;
  const projectPOV = selectedBook.povTense.pov?.value;
  const projectTense = selectedBook.povTense.tense?.value;
  const projectPOVCharacters = selectedBook.povTense.pov?.characters;
  const updateItemPovTense = useStructuredOutlineStore((state) => state.updateItemPovTense);
  const povTenseMap = useStructuredOutlineStore((state) => state.povTenseMap);
  const povTenseMapLoaded = useStructuredOutlineStore((state) => state.povTenseMapLoaded);
  const scenes = useStoryBibleStore((state) => state.scenes);
  const documentChapter = useGetLinkedChapter(document?.id);
  const linkedChapter = selectedBook.isActiveProject ? documentChapter : undefined;

  const [povTense, setPOVTense] = useState({
    pov: { value: null, characters: [], userChanged: false },
    tense: { value: null, userChanged: false },
  });
  const containerRef = useRef(null);
  const isVisible = useOnScreen(containerRef, "200px");
  const selectedId = useSeriesStore((state) => state.selectedSeriesProjectId);
  const outline = useStructuredOutlineStore((state) => state.outline);
  const readOnly = useStructuredOutlineStore((state) => state.readOnly);
  const project = useProjectStore((state) => state.project);
  const generationRef = useRef(0);
  const editEpoch = useRef(0);
  const mounted = useRef(true);
  const [, selectionChanged] = useState(0);
  const ownerIdentity = `${projectId}/${outline?.id}/${outline?.documentId}/${chapter.id}/${selectedId}/${disabled}/${readOnly}`;
  const identity = `${ownerIdentity}/${chapter.povTenseId}`;
  const identityRef = useRef({ identity, ownerIdentity });
  const rowChanged = identityRef.current.identity !== identity && identityRef.current.ownerIdentity === ownerIdentity;
  if (identityRef.current.identity !== identity) {
    identityRef.current = { identity, ownerIdentity };
    generationRef.current++;
    editEpoch.current++;
  }
  const generation = generationRef.current;
  const [loadedGeneration, setLoadedGeneration] = useState(null);
  const savingRef = useRef({ generation, count: 0 });
  if (savingRef.current.generation !== generation) savingRef.current = { generation, count: 0 };
  const context = { projectId, outlineId: outline?.id, documentId: outline?.documentId };
  const ownsOutline = useCallback(() => {
    const live = useStructuredOutlineStore.getState();
    const active = useProjectStore.getState().project;
    const item = live.outline?.content?.items?.find((item) => item.id === chapter.id);
    return mounted.current && generation === generationRef.current && !disabled && !live.readOnly
      && active?.id === project?.id && live.outline?.projectId === projectId
      && live.outline?.id === outline?.id && live.outline?.documentId === outline?.documentId
      && item?.povTenseId === chapter.povTenseId && !!item
      && (!active?.seriesId || (useSeriesStore.getState().selectedSeriesProjectId === projectId
        && useSeriesStore.getState().contents.some((entry) => entry.projectId === projectId)));
  }, [generation, disabled, project?.id, projectId, outline?.id, outline?.documentId, chapter.id, chapter.povTenseId]);
  const canEdit = () => ownsOutline() && selectedBook.settingsReady && loadedGeneration === generation;
  useEffect(() => {
    mounted.current = true;
    const invalidate = () => { editEpoch.current++; };
    const unsubscribe = useSeriesStore.subscribe((state, previous) => {
      if (state.selectedSeriesProjectId !== previous.selectedSeriesProjectId) {
        generationRef.current++;
        invalidate();
        selectionChanged((value) => value + 1);
      }
    });
    return () => { mounted.current = false; invalidate(); unsubscribe(); };
  }, []);
  const povTenseRef = useRef(povTense);
  povTenseRef.current = povTense;
  const { setToast } = useToast();

  // Fill in the project default when a stored field is null/missing — handles rows damaged by
  // an earlier bug that PUT tense: null. The user sees a sensible value and any subsequent edit
  // persists the project default into the row, self-healing the data.
  const withDefaults = useCallback(
    (stored) => ({
      pov: stored?.pov?.value
        ? stored.pov
        : { value: projectPOV, characters: projectPOVCharacters || [], userChanged: false },
      tense: stored?.tense?.value
        ? stored.tense
        : { value: projectTense, userChanged: false },
    }),
    [projectPOV, projectTense, projectPOVCharacters],
  );

  // Creation attaches the row and caches its values together, before linked-document
  // saves finish. Initialize during this render so the controls never unmount.
  // A replacement still invalidates old callbacks and can only use its own cache.
  if (rowChanged && multiplePOV && selectedBook.settingsReady && ownsOutline() && povTenseMap?.[chapter.povTenseId]) {
    setPOVTense(withDefaults(povTenseMap[chapter.povTenseId]));
    setLoadedGeneration(generation);
  }

  const updateCurrentDocumentPOVTense = (povTense) => {
    if (!ownsOutline() || useProjectStore.getState().project?.id !== projectId || useProjectStore.getState().document?.id !== document?.id) return;
    const documentScene = Object.values(scenes).find((scene) => scene.contents.type === "document" && scene.contents.id === document?.id);
    if (!documentScene) {
      return;
    }

    if (documentScene.itemId === chapter.id) {
      sendAppEvent("updating_current_document_pov_tense_from_chapter", {
        povTense,
        projectId,
      });
      setCurrentDocumentPOVTense(povTense);
    }
  };

  const savePatch = async (patch, feature, message) => {
    if (!canEdit()) return;
    const epoch = ++editEpoch.current;
    const previous = povTenseRef.current;
    const next = { ...previous, ...patch };
    const isCurrent = () => ownsOutline() && epoch === editEpoch.current;
    const saving = savingRef.current;
    povTenseRef.current = next;
    setPOVTense(next);
    updateCurrentDocumentPOVTense(next);
    saving.count++;
    try {
      await updateItemPovTense(chapter.id, chapter.povTenseId, next, scenes, { ...context, isCurrent });
    } catch (error) {
      Sentry.captureException(error, { tags: { feature } });
      if (isCurrent()) {
        setToast(message);
        povTenseRef.current = previous;
        setPOVTense(previous);
        updateCurrentDocumentPOVTense(previous);
      }
    } finally {
      saving.count--;
    }
  };

  const handleManualTenseChange = (selectedOption) => {
    if (!canEdit()) return;
    sendAppEvent("changing_chapter_tense", { tense: selectedOption, projectId });
    return savePatch({ tense: { value: typeof selectedOption === "object" ? selectedOption?.value : selectedOption, userChanged: true } },
      "structured_outline_chapter_tense_update", "Unable to update tense. Please try again.");
  };

  const handleManualPOVChange = (selectedOption) => {
    if (!canEdit()) return;
    sendAppEvent("changing_chapter_pov", { pov: selectedOption, projectId });
    const isAuto = selectedOption === POVS.THIRD_PERSON_OMNISCIENT || isAutoPOV(selectedOption);
    return savePatch({ pov: { value: selectedOption, characters: isAuto ? [] : povTenseRef.current?.pov?.characters || [], userChanged: true } },
      "structured_outline_chapter_pov_update", "Unable to update POV. Please try again.");
  };

  const handleCharacterSelectChange = (selectedOptions) => {
    if (!canEdit()) return;
    const value = povTenseRef.current?.pov?.value;
    return savePatch({ pov: { value: isAutoPOV(value) && selectedOptions.length ? POVS.THIRD_PERSON_LIMITED : value, characters: selectedOptions, userChanged: true } },
      "structured_outline_chapter_pov_character_update", "Unable to update POV characters. Please try again.");
  };

  useEffect(() => {
    if (savingRef.current.count > 0 || !multiplePOV || !selectedBook.settingsReady || !ownsOutline()) return;
    let cancelled = false;
    const epoch = editEpoch.current;
    const install = (stored) => {
      if (cancelled || !ownsOutline() || epoch !== editEpoch.current) return;
      const values = withDefaults(stored);
      povTenseRef.current = values;
      setPOVTense(values);
      setLoadedGeneration(generation);
    };
    const load = async () => {
      if (chapter.povTenseId) {
        const cached = povTenseMap?.[chapter.povTenseId];
        if (cached) { install(cached); return; }
        if (!povTenseMapLoaded || !isVisible) return;
        install(await Auth.fetchWithIdToken(`/api/povTense/${chapter.povTenseId}`));
      } else {
        install(linkedChapter?.id === chapter.id ? currentDocumentPOVTense : null);
      }
    };
    load().catch((error) => { if (!cancelled) Sentry.captureException(error, { tags: { feature: "structured_outline_chapter_pov_load" } }); });
    return () => { cancelled = true; };
  }, [chapter, projectId, linkedChapter, currentDocumentPOVTense, document, multiplePOV, isVisible, povTenseMap, povTenseMapLoaded, withDefaults, generation, selectedBook.settingsReady, ownsOutline]);

  if (!multiplePOV) return null;

  return (
    <div ref={containerRef}>
      {canEdit() && <TenseAndPOV
        pov={multiplePOV ? povTense?.pov : projectPOV}
        tense={multiplePOV ? povTense?.tense : projectTense}
        handleManualTenseChange={handleManualTenseChange}
        handleManualPOVChange={handleManualPOVChange}
        handleCharacterSelectChange={handleCharacterSelectChange}
        className={`${allowWrap ? "flex-wrap" : ""}`}
        collisionBoundarySelector=".project-main"
        oneOrManyCharacters="many"
        showPOVLabel={false}
        showTenseLabel={false}
      />}
    </div>
  );
});
